// src/admin/AddNewStudent.js
import React, { useState, useEffect } from 'react';
import { firestore } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  writeBatch,
  orderBy
} from 'firebase/firestore';
import { generateStudentId } from '../utils';
import './AddNewStudent.css';

const AddNewStudent = () => {
  // 1. THÔNG TIN BẮT BUỘC
  const [holyName, setHolyName] = useState('');
  const [fullName, setFullName] = useState('');
  const [gender, setGender] = useState('nam'); // 'nam' hoặc 'nu'
  const [selectedClassId, setSelectedClassId] = useState('');

  // 2. TRẠNG THÁI MỞ RỘNG SƠ YẾU LÝ LỊCH
  const [showExtendedInfo, setShowExtendedInfo] = useState(false);

  // 3. CÁC TRƯỜNG THÔNG TIN MỞ RỘNG
  const [extraData, setExtraData] = useState({
    dateOfBirth: '',
    placeOfBirth: '',
    baptismDate: '',
    baptismPlace: '',
    firstCommunionDate: '',
    confirmationDate: '',
    fatherName: '',
    fatherBirthYear: '',
    fatherJob: '',
    motherName: '',
    motherBirthYear: '',
    motherJob: '',
    address: '',
    subParish: '',
    phone1: '',
    phone2: ''
  });

  // State Niên khóa & Lớp học động từ Firestore
  const [currentYearId, setCurrentYearId] = useState('');
  const [currentYearName, setCurrentYearName] = useState('');
  const [classesList, setClassesList] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  // State UI Modal & Thông báo
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [error, setError] = useState('');

  // ====================================================================
  // TẢI DANH SÁCH LỚP HỌC THEO NIÊN KHÓA HIỆN TẠI
  // ====================================================================
  useEffect(() => {
    const fetchCurrentYearAndClasses = async () => {
      try {
        setLoadingData(true);
        const yearQuery = query(collection(firestore, 'academic_years'), where('isCurrent', '==', true));
        let yearSnap = await getDocs(yearQuery);
        let currentYearDoc = yearSnap.docs[0];

        if (!currentYearDoc) {
          const allYearsSnap = await getDocs(query(collection(firestore, 'academic_years'), orderBy('startDate', 'desc')));
          if (!allYearsSnap.empty) {
            currentYearDoc = allYearsSnap.docs[0];
          }
        }

        if (!currentYearDoc) {
          setError('Hệ thống chưa thiết lập Niên khóa nào. Vui lòng tạo Niên khóa trước.');
          setLoadingData(false);
          return;
        }

        const yId = currentYearDoc.id;
        const yData = currentYearDoc.data();
        setCurrentYearId(yId);
        setCurrentYearName(yData.name || yId);

        // Tải danh sách Cấp Lớp (Grades) để lấy thứ tự (sortOrder)
        const gradesSnap = await getDocs(collection(firestore, `academic_years/${yId}/grades`));
        const gradeOrderMap = {};
        gradesSnap.docs.forEach(d => {
          gradeOrderMap[d.id] = d.data().sortOrder || 0;
        });

        // Tải danh sách Lớp học
        const classesSnap = await getDocs(collection(firestore, `academic_years/${yId}/classes`));
        const list = classesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        list.sort((a, b) => {
          const orderA = gradeOrderMap[a.gradeId] ?? 999;
          const orderB = gradeOrderMap[b.gradeId] ?? 999;
          if (orderA !== orderB) return orderA - orderB;
          return (a.sortOrder || 0) - (b.sortOrder || 0);
        });

        setClassesList(list);
      } catch (err) {
        console.error('Lỗi khi tải danh sách lớp học:', err);
        setError('Lỗi khi tải dữ liệu niên khóa hiện tại.');
      } finally {
        setLoadingData(false);
      }
    };

    fetchCurrentYearAndClasses();
  }, []);

  // Tự động gợi ý giới tính theo tên lớp khi chọn
  const handleClassChange = (e) => {
    const classId = e.target.value;
    setSelectedClassId(classId);

    const selectedClassObj = classesList.find(c => c.id === classId);
    const classIdLower = classId.toLowerCase();
    const classNameLower = (selectedClassObj?.name || '').toLowerCase();

    if (classIdLower.endsWith('-nu') || classIdLower.includes('-nu-') || classNameLower.includes('nữ')) {
      setGender('nu');
    } else if (classIdLower.endsWith('-nam') || classIdLower.includes('-nam-') || classNameLower.includes('nam')) {
      setGender('nam');
    }
  };

  const handleExtraChange = (field, value) => {
    setExtraData(prev => ({ ...prev, [field]: value }));
  };

  // ====================================================================
  // XỬ LÝ LƯU THIẾU NHI MỚI VÀO FIRESTORE
  // ====================================================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const cleanHolyName = holyName.trim();
    const cleanFullName = fullName.trim();

    if (!cleanHolyName || !cleanFullName || !selectedClassId || !gender) {
      setError('Vui lòng điền đầy đủ Tên Thánh, Họ và Tên, Giới tính và Lớp học.');
      return;
    }

    if (!currentYearId) {
      setError('Không xác định được Niên khóa hiện tại.');
      return;
    }

    setIsSubmitting(true);

    try {
      const selectedClassObj = classesList.find(c => c.id === selectedClassId);

      // Tính số lượng thiếu nhi để sinh Mã ID
      const studentsSnap = await getDocs(collection(firestore, 'students'));
      let currentStudentCount = studentsSnap.size;
      let newStudentId = generateStudentId(cleanHolyName, cleanFullName, currentStudentCount);

      // Kiểm tra tránh trùng lặp Mã ID
      let attempts = 0;
      while (attempts < 10) {
        const checkDoc = await getDoc(doc(firestore, 'students', newStudentId));
        if (!checkDoc.exists()) break;
        currentStudentCount++;
        newStudentId = generateStudentId(cleanHolyName, cleanFullName, currentStudentCount);
        attempts++;
      }

      const batch = writeBatch(firestore);

      // (A) Lưu vào Hồ sơ gốc Thiếu Nhi: /students/{studentId}
      const studentDocRef = doc(firestore, 'students', newStudentId);
      batch.set(studentDocRef, {
        id: newStudentId,
        holyName: cleanHolyName,
        fullName: cleanFullName,
        gender: gender,
        status: 'Còn đi học',
        qrCode: null,
        // Dữ liệu Sơ Yếu Lý Lịch mở rộng
        dateOfBirth: extraData.dateOfBirth.trim() || '',
        placeOfBirth: extraData.placeOfBirth.trim() || '',
        baptismDate: extraData.baptismDate.trim() || '',
        baptismPlace: extraData.baptismPlace.trim() || '',
        firstCommunionDate: extraData.firstCommunionDate.trim() || '',
        confirmationDate: extraData.confirmationDate.trim() || '',
        fatherName: extraData.fatherName.trim() || '',
        fatherBirthYear: extraData.fatherBirthYear.trim() || '',
        fatherJob: extraData.fatherJob.trim() || '',
        motherName: extraData.motherName.trim() || '',
        motherBirthYear: extraData.motherBirthYear.trim() || '',
        motherJob: extraData.motherJob.trim() || '',
        address: extraData.address.trim() || '',
        subParish: extraData.subParish.trim() || '',
        phone1: extraData.phone1.trim() || '',
        phone2: extraData.phone2.trim() || '',
        createdAt: new Date().toISOString()
      });

      // (B) Lưu vào Phân lớp & Bảng điểm: /academic_years/{currentYearId}/enrollments/{studentId}
      const enrollmentDocRef = doc(firestore, `academic_years/${currentYearId}/enrollments`, newStudentId);
      batch.set(enrollmentDocRef, {
        studentId: newStudentId,
        classId: selectedClassId,
        gradeId: selectedClassObj?.gradeId || '',
        status: 'Đang học',
        transcript: {
          semester1: {},
          semester2: {},
          yearly: {}
        }
      });

      await batch.commit();

      setSubmittedData({
        id: newStudentId,
        holyName: cleanHolyName,
        fullName: cleanFullName,
        gender: gender === 'nu' ? 'Nữ 👧' : 'Nam 👦',
        className: selectedClassObj?.name || selectedClassId,
        dateOfBirth: extraData.dateOfBirth || 'Chưa nhập',
        yearName: currentYearName
      });
      setModalVisible(true);

      // Reset Form
      setHolyName('');
      setFullName('');
      setSelectedClassId('');
      setGender('nam');
      setExtraData({
        dateOfBirth: '',
        placeOfBirth: '',
        baptismDate: '',
        baptismPlace: '',
        firstCommunionDate: '',
        confirmationDate: '',
        fatherName: '',
        fatherBirthYear: '',
        fatherJob: '',
        motherName: '',
        motherBirthYear: '',
        motherJob: '',
        address: '',
        subParish: '',
        phone1: '',
        phone2: ''
      });
      setShowExtendedInfo(false);

    } catch (err) {
      console.error('Lỗi khi thêm thiếu nhi:', err);
      setError('Lỗi khi lưu dữ liệu lên Firestore: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setSubmittedData(null);
  };

  return (
    <div className="add-new-student-container admin-container">
      <h2>Thêm Thiếu Nhi Mới</h2>
      {currentYearName && (
        <p className="ans-sub-title">
          ⭐ Thêm vào Niên khóa: <strong>{currentYearName}</strong>
        </p>
      )}

      {error && <p className="error">{error}</p>}

      <form onSubmit={handleSubmit}>
        {/* KHỐI 1: CƠ BẢN (BẮT BUỘC) */}
        <div className="ans-grid-2">
          <div className="form-group">
            <label>Tên Thánh *</label>
            <input
              type="text"
              value={holyName}
              onChange={(e) => setHolyName(e.target.value)}
              placeholder="Ví dụ: Têrêsa, Giuse, Maria"
              required
            />
          </div>

          <div className="form-group">
            <label>Họ và Tên *</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Ví dụ: Nguyễn Văn A"
              required
            />
          </div>
        </div>

        <div className="ans-grid-2">
          <div className="form-group">
            <label>Giới Tính *</label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              required
            >
              <option value="nam">Nam 👦</option>
              <option value="nu">Nữ 👧</option>
            </select>
          </div>

          <div className="form-group">
            <label>Lớp Học ({currentYearName || 'Niên khóa hiện tại'}) *</label>
            <select
              value={selectedClassId}
              onChange={handleClassChange}
              disabled={loadingData}
              required
            >
              <option value="">-- Chọn lớp học --</option>
              {classesList.map((classItem) => (
                <option key={classItem.id} value={classItem.id}>
                  {classItem.name || classItem.id}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* NÚT MỞ RỘNG / THU GỌN SƠ YẾU LÝ LỊCH */}
        <div className="ans-expand-toggle-box">
          <button
            type="button"
            className={`ans-expand-btn ${showExtendedInfo ? 'expanded' : ''}`}
            onClick={() => setShowExtendedInfo(!showExtendedInfo)}
          >
            {showExtendedInfo ? '▲ Thu gọn Sơ Yếu Lý Lịch' : '▼ Mở rộng điền thêm Sơ Yếu Lý Lịch'}
          </button>
        </div>

        {/* KHỐI 2: THÔNG TIN MỞ RỘNG */}
        {showExtendedInfo && (
          <div className="ans-extended-panel">
            {/* Nhóm 1: Ngày Sinh & Nơi Sinh */}
            <div className="ans-section-divider">📌 Ngày Sinh & Nơi Sinh</div>
            <div className="ans-grid-2">
              <div className="form-group">
                <label>Sinh Ngày (DD/MM/YYYY)</label>
                <input
                  type="text"
                  placeholder="Ví dụ: 15/08/2014"
                  value={extraData.dateOfBirth}
                  onChange={(e) => handleExtraChange('dateOfBirth', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Nơi Sinh</label>
                <input
                  type="text"
                  placeholder="Ví dụ: TP.HCM"
                  value={extraData.placeOfBirth}
                  onChange={(e) => handleExtraChange('placeOfBirth', e.target.value)}
                />
              </div>
            </div>

            {/* Nhóm 2: Bí Tích */}
            <div className="ans-section-divider">🕊️ Thông Tin Bí Tích</div>
            <div className="ans-grid-2">
              <div className="form-group">
                <label>Ngày Rửa Tội (DD/MM/YYYY)</label>
                <input
                  type="text"
                  placeholder="Ví dụ: 20/09/2014"
                  value={extraData.baptismDate}
                  onChange={(e) => handleExtraChange('baptismDate', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Nơi Rửa Tội</label>
                <input
                  type="text"
                  placeholder="Ví dụ: Gx. An Nhơn"
                  value={extraData.baptismPlace}
                  onChange={(e) => handleExtraChange('baptismPlace', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Ngày Rước Lễ (DD/MM/YYYY)</label>
                <input
                  type="text"
                  placeholder="Ví dụ: 21/05/2023"
                  value={extraData.firstCommunionDate}
                  onChange={(e) => handleExtraChange('firstCommunionDate', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Ngày Thêm Sức (DD/MM/YYYY)</label>
                <input
                  type="text"
                  placeholder="Ví dụ: 08/06/2025"
                  value={extraData.confirmationDate}
                  onChange={(e) => handleExtraChange('confirmationDate', e.target.value)}
                />
              </div>
            </div>

            {/* Nhóm 3: Gia Đình */}
            <div className="ans-section-divider">👨‍👩‍👧 Thông Tin Gia Đình</div>
            <div className="ans-grid-3">
              <div className="form-group">
                <label>Tên Cha</label>
                <input
                  type="text"
                  placeholder="Ví dụ: Giuse Nguyễn Văn B"
                  value={extraData.fatherName}
                  onChange={(e) => handleExtraChange('fatherName', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Năm Sinh Cha</label>
                <input
                  type="text"
                  placeholder="Ví dụ: 1980"
                  value={extraData.fatherBirthYear}
                  onChange={(e) => handleExtraChange('fatherBirthYear', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Nghề Nghiệp Cha</label>
                <input
                  type="text"
                  placeholder="Ví dụ: Kinh doanh"
                  value={extraData.fatherJob}
                  onChange={(e) => handleExtraChange('fatherJob', e.target.value)}
                />
              </div>
            </div>

            <div className="ans-grid-3">
              <div className="form-group">
                <label>Tên Mẹ</label>
                <input
                  type="text"
                  placeholder="Ví dụ: Maria Trần Thị C"
                  value={extraData.motherName}
                  onChange={(e) => handleExtraChange('motherName', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Năm Sinh Mẹ</label>
                <input
                  type="text"
                  placeholder="Ví dụ: 1984"
                  value={extraData.motherBirthYear}
                  onChange={(e) => handleExtraChange('motherBirthYear', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Nghề Nghiệp Mẹ</label>
                <input
                  type="text"
                  placeholder="Ví dụ: Giáo viên"
                  value={extraData.motherJob}
                  onChange={(e) => handleExtraChange('motherJob', e.target.value)}
                />
              </div>
            </div>

            {/* Nhóm 4: Địa Chỉ & Liên Hệ */}
            <div className="ans-section-divider">🏠 Địa Chỉ & Liên Hệ</div>
            <div className="ans-grid-2">
              <div className="form-group span-full">
                <label>Địa Chỉ</label>
                <input
                  type="text"
                  placeholder="Ví dụ: 123/4 Lê Đức Thọ, P.17, Gò Vấp"
                  value={extraData.address}
                  onChange={(e) => handleExtraChange('address', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Giáo Khu</label>
                <input
                  type="text"
                  placeholder="Ví dụ: Phêrô, Mân Côi"
                  value={extraData.subParish}
                  onChange={(e) => handleExtraChange('subParish', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Điện Thoại 1</label>
                <input
                  type="text"
                  placeholder="Ví dụ: 0987654321"
                  value={extraData.phone1}
                  onChange={(e) => handleExtraChange('phone1', e.target.value)}
                />
              </div>
              <div className="form-group span-full">
                <label>Điện Thoại 2</label>
                <input
                  type="text"
                  placeholder="Ví dụ: 0912345678"
                  value={extraData.phone2}
                  onChange={(e) => handleExtraChange('phone2', e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        <button type="submit" className="submit-button" disabled={isSubmitting || loadingData}>
          {isSubmitting ? '⏳ Đang lưu Firestore...' : '+ Thêm Thiếu Nhi'}
        </button>
      </form>

      {/* Modal hiển thị sau khi lưu thành công */}
      {modalVisible && submittedData && (
        <div className="modal">
          <div className="ans-modal-content">
            <h3>🎉 Đã Thêm Thiếu Nhi Thành Công!</h3>
            <p><strong>Mã ID:</strong> <span className="ans-highlight-id">{submittedData.id}</span></p>
            <p><strong>Tên Thánh:</strong> {submittedData.holyName}</p>
            <p><strong>Họ và Tên:</strong> {submittedData.fullName}</p>
            <p><strong>Giới Tính:</strong> {submittedData.gender}</p>
            <p><strong>Lớp:</strong> {submittedData.className}</p>
            <p><strong>Niên Khóa:</strong> {submittedData.yearName}</p>
            <p><strong>Ngày Sinh:</strong> {submittedData.dateOfBirth}</p>
            <button onClick={closeModal}>Đóng</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddNewStudent;